import React, { useMemo } from 'react';
import { useLanguage } from '@/state/language/LanguageProvider';
import { cn } from '@/utils/cn';

export type CalendarRsvpValue = 'none' | 'accepted' | 'declined' | 'tentative';

/** Coerce API/legacy strings to a known RSVP value. */
export function normalizeCalendarRsvpValue(raw: string | null | undefined): CalendarRsvpValue {
  const s = raw ?? 'none';
  if (s === 'accepted' || s === 'declined' || s === 'tentative' || s === 'none') return s;
  return 'none';
}

function selectedClass(value: Exclude<CalendarRsvpValue, 'none'>): string {
  switch (value) {
    case 'accepted':
      return 'border-emerald-600 bg-emerald-600 text-white shadow-sm dark:border-emerald-500 dark:bg-emerald-600';
    case 'tentative':
      return 'border-amber-500 bg-amber-500 text-amber-950 shadow-sm dark:border-amber-600 dark:bg-amber-600 dark:text-amber-50';
    case 'declined':
      return 'border-rose-600 bg-rose-600 text-white shadow-sm dark:border-rose-500 dark:bg-rose-600';
  }
}

const unselectedClass =
  'border border-[var(--color-border)] bg-transparent text-[var(--color-primary)] hover:bg-[var(--color-surface-elevated)] dark:hover:bg-[var(--color-surface-page)]';

type Props = {
  value: CalendarRsvpValue;
  onSelect: (v: CalendarRsvpValue) => void;
  /** Tighter padding and text for dashboard agenda rows. */
  compact?: boolean;
  className?: string;
  /** e.g. aria-labelledby */
  'aria-label'?: string;
  /** Rendered after the RSVP buttons on the same row (e.g. “Invited” badge). */
  suffix?: React.ReactNode;
};

/**
 * Invitee RSVP: Accept / Tentative / Decline. Unresponded API state shows no selection until the user picks one.
 */
export const CalendarRsvpChoice: React.FC<Props> = ({
  value,
  onSelect,
  compact = false,
  className,
  'aria-label': ariaLabel,
  suffix
}) => {
  const { t } = useLanguage();
  const current = value ?? 'none';

  const options = useMemo(
    () =>
      [
        { value: 'accepted' as const, label: t('calendar.rsvp.option.accept') },
        { value: 'tentative' as const, label: t('calendar.rsvp.option.tentative') },
        { value: 'declined' as const, label: t('calendar.rsvp.option.decline') }
      ] as const,
    [t]
  );

  return (
    <div
      className={cn('flex flex-wrap items-center gap-1', className)}
      role="group"
      aria-label={ariaLabel ?? t('calendar.modal.yourResponse')}
    >
      {options.map(({ value: v, label }) => {
        const selected = current !== 'none' && current === v;
        return (
          <button
            key={v}
            type="button"
            aria-pressed={selected}
            className={cn(
              'rounded-md font-medium transition-colors',
              compact ? 'px-1.5 py-0.5 text-[9px] sm:text-[10px]' : 'px-2.5 py-1.5 text-xs sm:text-sm',
              selected ? selectedClass(v) : unselectedClass
            )}
            onClick={(e) => {
              e.stopPropagation();
              onSelect(v);
            }}
          >
            {label}
          </button>
        );
      })}
      {suffix}
    </div>
  );
};
